import { createRouter, createWebHistory } from "vue-router";
import Accueil from "./screens/Accueil.vue";
import Fonctionnalites from "./screens/Fonctionnalites.vue";
import Tarifs from "./screens/Tarifs.vue";
import Contact from "./screens/Contact.vue";
import Login from "./screens/Login.vue";
import Inscription from "./screens/Inscription.vue";
import MotDePasseOublie from "./screens/MotDePasseOublie.vue";

type RouteSeoMeta = {
  title?: string;
  description?: string;
  /**
   * Image path relative to the build output root (e.g. "assets/og.webp"),
   * OR an absolute URL (https://...).
   */
  image?: string;
};

function resolveSeoAssetUrl(pathOrUrl: string): string {
  // Absolute URL stays as-is.
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

  // Ensure we don't accidentally create "/tarifs/assets/..." when publicPath is "./"
  // and the app is served under a history route.
  const rel = pathOrUrl.replace(/^\.?\//, "");

  if (typeof document === "undefined") return `/${rel}`;

  // Resolve from the actual script/css URL loaded by the browser so it matches the real publicPath
  // ("/" or "./" or a subfolder).
  const scripts = Array.from(document.getElementsByTagName("script"));
  const jsSrc =
    scripts.map((s) => s.src).find((src) => /\/js\/app\.[^/]+\.js$/i.test(src)) ||
    scripts.map((s) => s.src).find((src) => /\/js\/chunk-vendors\.[^/]+\.js$/i.test(src)) ||
    scripts.map((s) => s.src).find((src) => /\/js\//i.test(src));

  if (jsSrc) {
    const jsDir = new URL(".", jsSrc); // .../js/
    const rootDir = new URL("..", jsDir); // .../
    return new URL(rel, rootDir).toString();
  }

  const cssHref = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .map((l) => (l as HTMLLinkElement).href)
    .find(Boolean);
  if (cssHref) {
    const cssDir = new URL(".", cssHref); // .../css/
    const rootDir = new URL("..", cssDir); // .../
    return new URL(rel, rootDir).toString();
  }

  return new URL(rel, new URL(".", window.location.href)).toString();
}

const routes = [
  {
    path: "/",
    name: "Accueil",
    component: Accueil,
    meta: {
      title: "Florisuite | Logiciel de gestion pour fleuristes",
      description:
        "Florisuite est une suite tout-en-un pour les fleuristes : caisse, commandes, facturation, fidélisation et e‑commerce. Simplifiez la gestion de votre boutique et démarrez votre essai gratuit.",
      image: "assets/fonctionnalites/caisse.webp",
    } satisfies RouteSeoMeta,
  },
  {
    path: "/accueil",
    redirect: "/",
  },
  {
    path: "/fonctionnalites",
    name: "Fonctionnalites",
    component: Fonctionnalites,
    meta: {
      title: "Fonctionnalités | Florisuite",
      description:
        "Découvrez toutes les fonctionnalités de Florisuite : caisse, commandes, facturation, fidélisation, e‑commerce, réseaux sociaux et support.",
      image: "assets/fonctionnalites/commandes.webp",
    } satisfies RouteSeoMeta,
  },
  {
    path: "/tarifs",
    name: "Tarifs",
    component: Tarifs,
    meta: {
      title: "Tarifs | Florisuite",
      description:
        "Des tarifs adaptés à vos besoins. Comparez les plans Florisuite et démarrez votre essai gratuit dès aujourd’hui.",
      image: "assets/fonctionnalites/facturation.webp",
    } satisfies RouteSeoMeta,
  },
  {
    path: "/contact",
    name: "Contact",
    component: Contact,
    meta: {
      title: "Contact | Florisuite",
      description:
        "Contactez l’équipe Florisuite : support, démo et questions commerciales. Nous répondons rapidement.",
      image: "assets/fonctionnalites/support.webp",
    } satisfies RouteSeoMeta,
  },
  {
    path: "/connexion",
    name: "Login",
    component: Login,
    meta: {
      title: "Connexion | Florisuite",
      description: "Connectez-vous à votre compte Florisuite.",
      image: "assets/fonctionnalites/caisse.webp",
    } satisfies RouteSeoMeta,
  },
  {
    path: "/inscription",
    name: "Inscription",
    component: Inscription,
    meta: {
      title: "Inscription | Florisuite",
      description:
        "Créez votre compte Florisuite et démarrez votre essai gratuit. Aucune carte bancaire requise.",
      image: "assets/fonctionnalites/commandes.webp",
    } satisfies RouteSeoMeta,
  },
  {
    path: "/mot-de-passe-oublie",
    name: "MotDePasseOublie",
    component: MotDePasseOublie,
    meta: {
      title: "Mot de passe oublié | Florisuite",
      description:
        "Réinitialisez votre mot de passe Florisuite en recevant un lien par email.",
      image: "assets/fonctionnalites/support.webp",
    } satisfies RouteSeoMeta,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function upsertMetaTag(selector: string, attrs: Record<string, string>) {
  if (typeof document === "undefined") return;
  const existing = document.head.querySelector(selector);
  const el = (existing ?? document.createElement("meta")) as HTMLMetaElement;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  if (!existing) document.head.appendChild(el);
}

router.afterEach((to) => {
  if (typeof document === "undefined") return;

  const meta = (to.meta ?? {}) as RouteSeoMeta;
  if (meta.title) document.title = meta.title;

  if (meta.description) {
    upsertMetaTag('meta[name="description"]', {
      name: "description",
      content: meta.description,
    });
    upsertMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: meta.description,
    });
    upsertMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: meta.description,
    });
  }

  if (meta.title) {
    upsertMetaTag('meta[property="og:title"]', { property: "og:title", content: meta.title });
    upsertMetaTag('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
  }

  if (meta.image) {
    const img = resolveSeoAssetUrl(meta.image);
    upsertMetaTag('meta[property="og:image"]', { property: "og:image", content: img });
    upsertMetaTag('meta[name="twitter:image"]', { name: "twitter:image", content: img });
  }

  // Used by prerender-spa-plugin to know when the page is ready.
  document.dispatchEvent(new Event("prerender-ready"));
});

export default router;
